import React, { useEffect, useState } from 'react';
import { useAtomValue } from 'jotai';
import Container from '@mui/material/Container';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import Grid from '@mui/material/Grid';
import lzSCommonsFormErrorsAtom from '@lz-atoms/Commons/formErrors';
import { lzFInitials } from '@lz/services/String';
import { useLzFShowMediaBrowser } from '@lz/services/Utils';
import LzMBase, { LzTBase } from '@lz/models/commons/Base';
import LzMMedia from '@lz/models/commons/Media';
import LzMCrudField, { LzTCrudFieldGroup } from '@lz/models/crud/Field';
import LzMCrudInput, { LzTCrudInputGroup } from '@lz/models/crud/Input';
import LzXForm from '../forms/Form';
import LzXMedia from '../commons/Media';
import { LzTFormSizes, LzTFunctionSimple, LzTOpenObject } from '@lz/typings/types';
import LzXAutocompleteField from '../forms/fields/AutocompleteField';
import LzXInputField from '../forms/fields/InputField';
import LzXRicheditorField from '../forms/fields/RicheditorField';
import LzXSelectField from '../forms/fields/SelectField';
import LzXStaticField from '../forms/fields/StaticField';
import LzXSwitchField from '../forms/fields/SwitchField';
import LzXTagsField from '../forms/fields/TagsField';
import LzXTextareaField from '../forms/fields/TextareaField';
import LzXMediaQuickUploader from '../commons/Media/QuickUploader';

export interface LzPCrudForm {
    fields: LzTCrudFieldGroup;
    inputs: LzTCrudInputGroup;
    modelRef: LzTBase;
    model: LzMBase;
    isTreeBased?: boolean;
    formSize?: LzTFormSizes;
    onCancel?: LzTFunctionSimple;
    onSubmit?: (model: LzMBase) => Promise<boolean>;
    onReset?: (model: LzMBase) => LzMBase;
    onReload?: LzTFunctionSimple;
    resetOnSubmit?: boolean;
    onChange?: (model: LzMBase | null) => void;
    className?: string;
}

const LzXCrudForm = (props: LzPCrudForm) => {
    if (typeof props.fields === 'undefined' || !props.fields || typeof props.inputs === 'undefined' || !props.inputs) {
        return (
            <Alert variant="filled" severity="error">
                CRUD configurations are not set!
            </Alert>
        );
    }

    if (typeof props.modelRef === 'undefined' || !props.modelRef) {
        return (
            <Alert variant="filled" severity="error">
                Model definition is missing!
            </Alert>
        );
    }

    if (!props.model) {
        return <CircularProgress />;
    }

    const [model, setModel] = useState<LzMBase | null>(null);
    const [QuickUploaderFieldKey, setQuickUploaderFieldKey] = useState<string>('');

    useEffect(() => {
        // console.log('FAM', Object.assign({}, props.model));
        setModel(LzMBase.buildModel(props.model, props.modelRef));
    }, [props.model, props.modelRef]);

    const formErrorsStateState = useAtomValue(lzSCommonsFormErrorsAtom);

    const lzFShowMediaBrowser = useLzFShowMediaBrowser();

    const handleFormReset = () => {
        setModel(LzMBase.buildModel(props.model, props.modelRef));
    };

    const handleFormSubmit: React.FormEventHandler<HTMLFormElement> = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (props.onSubmit && model) {
            const success = await props.onSubmit(model);
            if (success) {
                // If a custom reset function is provided
                if (typeof props.onReset !== 'undefined') {
                    setModel(props.onReset(model));
                }
                // If allowed to do reset
                else if (props.resetOnSubmit) {
                    handleFormReset();
                }
                // None of above means form will remain the same
            }
        }
    };

    const handleInputChange = (key: string, value: any) => {
        const updated = LzMBase.buildModel(Object.assign({}, model), props.modelRef);

        const input = props.inputs[key];
        if (typeof input.onChange === 'function') {
            updated[key] = input.onChange(key, value);
        } else {
            updated[key] = value;
        }

        setModel(updated);
    };

    const handleInputChanges = (updates: LzTOpenObject) => {
        setModel(
            LzMBase.buildModel(
                Object.assign({}, model, updates || {}),
                props.modelRef
            )
        );
    };

    const handleRemoveMedia = (key: string) => {
        const updated = LzMBase.buildModel(Object.assign({}, model), props.modelRef);
        updated[key] = null;
        updated[key + '_id'] = 0;
        setModel(updated);
    };

    const handleUploadMedia = (key: string, field: LzMCrudField) => {
        // If opted to use media browser
        if (field.mediaUseBrowser) {
            lzFShowMediaBrowser(
                {
                    category: field.mediaCategory,
                    file_type: field.mediaFileType,
                    content_type: field.mediaContentType,
                    is_private: field.mediaIsPrivate,
                },
                (media: LzMMedia) => {
                    const updated = LzMBase.buildModel(Object.assign({}, model), props.modelRef);
                    updated[key] = media;
                    updated[key + '_id'] = media.id;
                    setModel(updated);
                },
                (msg: string) => {
                    // TODO: Show error message if required
                    console.error('Media-Upload-Err', msg);
                },
            );
        }

        // Otherwise use simple quick uploader
        else {
            setQuickUploaderFieldKey(key);
        }
    };

    const handleOnUploaded = (medias: Array<LzMBase>, filesErrors: Array<string>) => {
        // Process uploaded media files
        if (medias.length > 0) {
            const updated = LzMBase.buildModel(Object.assign({}, model), props.modelRef);
            updated[QuickUploaderFieldKey] = new LzMMedia(medias[0] as LzMMedia);
            updated[QuickUploaderFieldKey + '_id'] = medias[0].id;
            setModel(updated);
        }

        // After upload is done, hide the de-activate upload
        setQuickUploaderFieldKey('');
    };

    useEffect(() => {
        if (props.onChange) {
            props.onChange(model);
        }
    }, [model]);

    const inputKeys = Object.keys(props.inputs);

    return (
        <React.Fragment>
            <div className={
                'lz-form-page lz-form-page-' + (props.formSize ? props.formSize : 'full')
                + (props.className ? ' ' + props.className : '')
            }>
                <Container>
                    <LzXForm onReset={handleFormReset} onCancel={props.onCancel} cancelLabel="Close" onSubmit={handleFormSubmit}>
                        {model ? (
                            <React.Fragment>
                                {QuickUploaderFieldKey !== '' && (
                                    <LzXMediaQuickUploader stealth onUploaded={handleOnUploaded} onCancel={() => setQuickUploaderFieldKey('')} />
                                )}

                                <Grid container spacing={2}>
                                    {inputKeys.map((key, dex) => {
                                        const field = new LzMCrudField(props.fields[key], key);
                                        const input = new LzMCrudInput(props.inputs[key]);
                                        const value = model[key];
                                        // console.log('Field-Val', key, value);

                                        // Skip if requested to
                                        if (input.skip) {
                                            return;
                                        }

                                        // If its a tree based crud and this is the parent field with type set to static (display only)
                                        if ('parent_id' === key && props.isTreeBased && 'static' === input.type) {
                                            return (
                                                <Grid size={{ sm: 6, md: 4 }} key={'inputField_' + dex}>
                                                    {input.isVisibleFunction(value, model, key) && (
                                                        <React.Fragment>
                                                            <LzXStaticField fluid icon={input.icon} label={input.label} value={model.parent_name} />
                                                        </React.Fragment>
                                                    )}
                                                </Grid>
                                            );
                                        }

                                        const mediaCalcs = { altField: '', altValue: '', initials: '' };
                                        if (input.isMedia) {
                                            mediaCalcs.altField = input.mediaParams?.altFieldName || '';
                                            mediaCalcs.altValue = '' === mediaCalcs.altField ? '' : model[mediaCalcs.altField] || '';
                                            mediaCalcs.initials = '' === mediaCalcs.altValue ? '' : lzFInitials(mediaCalcs.altValue);
                                        }

                                        return (
                                            <Grid size={{ sm: input.gridSpanSm, md: input.gridSpanMd }} key={'inputField_' + dex}>
                                                {input.isVisibleFunction(value, model, key) && (
                                                    <React.Fragment>
                                                        {'media' === input.type && (
                                                            <LzXMedia
                                                                isAvatar={!!input.mediaParams?.isAvatar}
                                                                avatarSize={input.mediaParams?.avatarSize || 'large'}
                                                                avatarInitials={mediaCalcs.initials}
                                                                altText={mediaCalcs.altValue}
                                                                imgSrc={value ? value.file_preview_path : ''}
                                                                label={input.label}
                                                                className="lz-card-media-small"
                                                                onRemove={() => handleRemoveMedia(key)}
                                                                onUpload={() => handleUploadMedia(key, field)}
                                                            />
                                                        )}

                                                        {'static' === input.type && (
                                                            <LzXStaticField
                                                                fluid
                                                                icon={input.icon}
                                                                label={input.label}
                                                                isReactNode={input.isReactNode}
                                                                value={input.displayFunction(
                                                                    value,
                                                                    model,
                                                                    key,
                                                                    (updates) => handleInputChanges(updates),
                                                                    props.onReload,
                                                                )}
                                                            />
                                                        )}

                                                        {['text', 'email', 'number', 'password', 'color', 'date'].indexOf(input.type) >= 0 && (
                                                            <LzXInputField
                                                                fluid
                                                                required={input.required}
                                                                multiline={input.multiline}
                                                                variant={input.variant}
                                                                type={input.subType !== '' ? input.subType : input.type}
                                                                icon={input.icon}
                                                                label={input.label}
                                                                placeholder={'Enter ' + input.label}
                                                                hintText={input.hintText || 'Enter ' + input.label}
                                                                name={key}
                                                                value={value}
                                                                onChange={handleInputChange}
                                                                formErrors={formErrorsStateState}
                                                            />
                                                        )}

                                                        {'textarea' === input.type && (
                                                            <LzXTextareaField
                                                                fluid
                                                                required={input.required}
                                                                icon={input.icon}
                                                                label={input.label}
                                                                placeholder={'Enter ' + input.label}
                                                                name={key}
                                                                value={value}
                                                                onChange={handleInputChange}
                                                            />
                                                        )}

                                                        {'richeditor' === input.type && (
                                                            <LzXRicheditorField
                                                                fluid
                                                                required={input.required}
                                                                icon={input.icon}
                                                                label={input.label}
                                                                placeholder={'Enter ' + input.label}
                                                                name={key}
                                                                value={value}
                                                                onChange={handleInputChange}
                                                                initiallyShowHtml
                                                            />
                                                        )}

                                                        {'switch' === input.type && (
                                                            <LzXSwitchField
                                                                fluid
                                                                label={input.label}
                                                                name={key}
                                                                value={value}
                                                                onChange={handleInputChange}
                                                            />
                                                        )}

                                                        {'select' === input.type && (
                                                            <LzXSelectField
                                                                fluid
                                                                required={input.required}
                                                                variant={input.variant}
                                                                icon={input.icon}
                                                                label={input.label}
                                                                placeholder={'Select ' + input.label}
                                                                name={key}
                                                                value={value}
                                                                noFirstOption={field.default !== '' && field.default !== 0}
                                                                options={input.options}
                                                                refTableName={field.refTableName}
                                                                refTableIdField={field.refTableIdField}
                                                                refTableTitleField={field.refTableTitleField}
                                                                refFilterFieldKeys={field.refFilterFieldKeys}
                                                                modelToFilter={model}
                                                                onChange={handleInputChange}
                                                            />
                                                        )}

                                                        {'tags' === input.type && (
                                                            <LzXTagsField
                                                                fluid
                                                                required={input.required}
                                                                lazyLoad={input.lazyLoad}
                                                                maxLimit={input.maxLimit}
                                                                icon={input.icon}
                                                                label={input.label}
                                                                placeholder="search..."
                                                                name={key}
                                                                value={value}
                                                                refTableName={field.refTableName}
                                                                refTableIdField={field.refTableIdField}
                                                                refTableTitleField={field.refTableTitleField}
                                                                refFilterFieldKeys={field.refFilterFieldKeys}
                                                                modelToFilter={model}
                                                                onChange={handleInputChange}
                                                            />
                                                        )}

                                                        {'autocomplete' === input.type && (
                                                            <LzXAutocompleteField
                                                                fluid
                                                                required={input.required}
                                                                lazyLoad={input.lazyLoad}
                                                                maxLimit={input.maxLimit}
                                                                variant={input.variant}
                                                                icon={input.icon}
                                                                label={input.label}
                                                                placeholder="search..."
                                                                name={key}
                                                                value={value}
                                                                refTableName={field.refTableName}
                                                                refTableIdField={field.refTableIdField}
                                                                refTableTitleField={field.refTableTitleField}
                                                                refFilterFieldKeys={field.refFilterFieldKeys}
                                                                modelToFilter={model}
                                                                onChange={handleInputChange}
                                                            />
                                                        )}
                                                    </React.Fragment>
                                                )}
                                            </Grid>
                                        );
                                    })}
                                </Grid>
                            </React.Fragment>
                        ) : (
                            <CircularProgress />
                        )}
                    </LzXForm>
                </Container>
            </div>
        </React.Fragment>
    );
};

export default LzXCrudForm;
